// "Paid to us by the sales rep" (admin): shared React pieces. Used by the admin
// invoice modal (RepRemittanceSection), the Orders tab (summary line, pill via
// remittancePill) and both Commission Mark Paid sites (PayoutRemitGate, RemitBadge).
// Self-contained palette (same approach as commissionLedgerUi.js) so App.js does
// not have to be imported. Every write goes through remittanceActions.js
// (Supabase first, awaited; the local mirror only afterwards). No browser storage is used here.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { listRemittances, subscribeRemittances, loadRemittanceContext, getActiveRemittanceForInvoiceStrict, getCommissionPaidStateForInvoice } from "./supabase";
import { createFetchGate, createDebounced, subscribeLocalRemittanceChange } from "./remittanceSync";
import { currentAdminIdentity } from "./commissionLedgerUi";
import { performMarkRemitted, performReverseRemittance } from "./remittanceActions";
import {
  REMIT_METHODS, isRepInvoice, methodLabel, todayIso, validateRemittanceInput, paymentSourceOf,
  commissionPayoutGate, commissionRemitBadge, remittanceSummary, remittanceBucketText, fmtRemitMoney, remittancePaidLabel,
  reverseCommissionGate, amountChangedMessage,
} from "./repRemittances";

const K = {
  ink: "#0F172A", inkMid: "#334155", inkLight: "#64748B", border: "#E2E8F0", surface: "#F8FAFC", white: "#FFFFFF",
  red: "#B91C1C", redLight: "#FEE2E2", amber: "#92400E", amberBg: "#FEF3C7", amberBorder: "#FCD34D",
  green: "#166534", greenBg: "#DCFCE7", greenBorder: "#86EFAC", blue: "#1560BD", blueLight: "#E8F0FE",
  violet: "#5B21B6", violetBg: "#EDE9FE", violetBorder: "#C4B5FD",
};
const box = (bg, border, fg) => ({ background: bg, border: "1px solid " + border, borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: fg, lineHeight: 1.5 });
const label = { display: "block", fontSize: 11, fontWeight: 600, color: K.inkMid, marginBottom: 4 };
const input = { width: "100%", padding: "8px 10px", border: "1px solid " + K.border, borderRadius: 8, fontSize: 13, color: K.ink, background: K.white, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
const btnPrimary = { background: K.violet, color: K.white, border: "none", padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnSecondary = { background: "transparent", color: K.ink, border: "1px solid " + K.border, padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer" };
const btnDanger = { background: K.red, color: K.white, border: "none", padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };

// ── data hook: the remittance list, fetched once per mount + kept fresh ──────
// A failed refresh KEEPS the previous rows but sets ok=false (fail-visible: the
// consumers then say the read failed instead of trusting stale rows).
//
// opts.enabled (default true): when false nothing is fetched and nothing is subscribed.
// A response is DROPPED when it is superseded by a newer fetch of this hook or when a local
// mark / reverse happened after it started (remittanceSync); a local change reloads every
// consumer at once, realtime bursts are collapsed into one fetch (500 ms debounce).
export function useRemittanceList(opts) {
  const enabled = !opts || opts.enabled !== false;
  const [state, setState] = useState({ rows: [], ok: null, error: "", missingTable: false });
  const alive = useRef(true);
  const gateRef = useRef(null);
  if (!gateRef.current) gateRef.current = createFetchGate();
  const reload = useCallback(async () => {
    const gate = gateRef.current;
    const ticket = gate.begin();
    try {
      const res = await listRemittances();
      if (!alive.current) return;
      if (!gate.isCurrent(ticket)) return;
      if (res.ok) setState({ rows: res.rows || [], ok: true, error: "", missingTable: !!res.missingTable });
      else {
        console.warn("[useRemittanceList] read failed; keeping the previous rows but marking the read as failed:", res.error);
        setState((s) => ({ rows: s.rows, ok: false, error: res.error || "read failed", missingTable: false }));
      }
    } catch (e) {
      console.warn("[useRemittanceList] read threw:", e?.message || e);
      if (alive.current && gate.isCurrent(ticket)) setState((s) => ({ rows: s.rows, ok: false, error: e?.message || String(e), missingTable: false }));
    }
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    alive.current = true;
    // Re-enabled (for example returning to a rep's Commission sub-tab): never evaluate the gate or badges
    // against the rows from the previous visit. Show Loading until the fresh read lands.
    setState((s) => (s.ok === null ? s : { rows: [], ok: null, error: "", missingTable: false }));
    reload();
    const deb = createDebounced(() => { reload(); });
    const unsub = subscribeRemittances(() => { deb.trigger(); });
    const unsubLocal = subscribeLocalRemittanceChange(() => { deb.cancel(); reload(); });
    return () => { alive.current = false; deb.cancel(); if (unsub) unsub(); unsubLocal(); };
  }, [reload, enabled]);
  return { ...state, reload };
}

// The ACTIVE remittance of ONE invoice (strict read, refreshed like the list hook), for the
// invoice modal header pill and paid text. Only rep invoices are read. ok=false keeps the previous value.
export function useActiveRemittance(inv) {
  const id = inv && inv.id ? String(inv.id) : "";
  const applies = !!id && isRepInvoice({ id, repCreated: !!(inv && inv.repCreated) });
  const [st, setSt] = useState({ rem: null, ok: null });
  const alive = useRef(true);
  const gateRef = useRef(null);
  if (!gateRef.current) gateRef.current = createFetchGate();
  const reload = useCallback(async () => {
    if (!applies) return;
    const gate = gateRef.current;
    const ticket = gate.begin();
    try {
      const r = await getActiveRemittanceForInvoiceStrict(id);
      if (!alive.current || !gate.isCurrent(ticket)) return;
      if (r.ok) setSt({ rem: r.remittance || null, ok: true });
      else setSt((s) => ({ rem: s.rem, ok: false }));
    } catch (e) {
      console.warn("[useActiveRemittance] read threw for", id, ":", e?.message || e);
      if (alive.current && gate.isCurrent(ticket)) setSt((s) => ({ rem: s.rem, ok: false }));
    }
  }, [applies, id]);
  useEffect(() => {
    alive.current = true;
    if (!applies) return undefined;
    reload();
    const deb = createDebounced(() => { reload(); });
    const unsub = subscribeRemittances(() => { deb.trigger(); });
    const unsubLocal = subscribeLocalRemittanceChange(() => { deb.cancel(); reload(); });
    return () => { alive.current = false; deb.cancel(); if (unsub) unsub(); unsubLocal(); };
  }, [applies, reload]);
  return applies ? st : { rem: null, ok: null };
}

// ── Orders tab: summary line ─────────────────────────────────────────────────
export function RemittanceSummaryLine({ remit }) {
  if (!remit || remit.missingTable) return null;
  if (remit.ok === false) {
    return <div style={box(K.redLight, "#FCA5A5", K.red)}>Payments received from sales reps could not be loaded ({remit.error}). The Paid via rep pills and totals may be out of date.</div>;
  }
  if (remit.ok === null) return null;
  const s = remittanceSummary(remit.rows);
  if (s.allTime.count === 0) return null;
  return (
    <div style={{ ...box(K.violetBg, K.violetBorder, K.violet), marginBottom: 12 }}>
      <strong>Received from sales reps.</strong>{" "}
      This month: {remittanceBucketText(s.thisMonth)}. All time: {remittanceBucketText(s.allTime)}.
    </div>
  );
}

// ── Commission tab: per-invoice badge ────────────────────────────────────────
export function RemitBadge({ inv, remit }) {
  if (!remit || remit.ok !== true || remit.missingTable) return null;
  const b = commissionRemitBadge(inv, remit.rows, true);
  if (!b) return null;
  const st = b.kind === "received"
    ? { background: K.violetBg, color: K.violet, border: "1px solid " + K.violetBorder }
    : b.kind === "paid_by_shop"
      ? { background: K.greenBg, color: K.green, border: "1px solid " + K.greenBorder }
      : { background: K.amberBg, color: K.amber, border: "1px solid " + K.amberBorder };
  const tip = b.kind === "received" ? "The company received this payment from the sales rep"
    : b.kind === "paid_by_shop" ? "The shop paid the company directly (card / ACH), not through the sales rep"
      : "No payment received from the sales rep is recorded for this invoice";
  return <span title={tip} style={{ ...st, display: "inline-block", fontSize: 9, fontWeight: 600, padding: "1px 7px", borderRadius: 8, whiteSpace: "nowrap" }}>{b.label}</span>;
}

// ── Commission Mark Paid: the WARN-ONLY gate ─────────────────────────────────
// gate: { kind, needsAck, loading, missingTable, lines, count, truncated }. Never a hard block:
// the admin ticks and continues. While the list is loading the confirm waits briefly.
export function payoutRemitGate(invoices, remit) {
  const r = remit || { ok: null, rows: [] };
  if (r.missingTable) return { kind: "none", needsAck: false, loading: false, missingTable: true, lines: [], count: 0, truncated: false };
  if (r.ok === null) return { kind: "none", needsAck: false, loading: true, missingTable: false, lines: [], count: 0, truncated: false };
  const g = commissionPayoutGate({ invoices, remittances: r.rows, readOk: r.ok === true });
  return { ...g, loading: false, missingTable: false, error: r.error || "" };
}

export function PayoutRemitGate({ gate, ack, setAck }) {
  if (!gate) return null;
  if (gate.loading) return <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 14 }}>Checking which invoices were paid to us by the sales rep...</div>;
  if (gate.missingTable) {
    return <div style={{ ...box(K.amberBg, K.amberBorder, K.amber), marginBottom: 14, border: "2px solid " + K.amber, fontSize: 13 }}><strong>Rep payments cannot be checked yet: migration 0033 has not been run.</strong> This payout is not checked against payments received from the rep (0033_invoice_rep_remittances.sql).</div>;
  }
  if (!gate.needsAck) return null;
  const failed = gate.kind === "readfailed";
  const tone = failed ? box(K.redLight, "#FCA5A5", K.red) : box(K.amberBg, K.amberBorder, K.amber);
  return (
    <div style={{ ...tone, marginBottom: 14 }}>
      {failed
        ? <div><strong>Payments received from the sales rep could not be checked</strong> ({gate.error || "read failed"}). These {gate.count} rep invoice{gate.count === 1 ? "" : "s"} may or may not have been paid to us:</div>
        : <div><strong>{gate.count} rep invoice{gate.count === 1 ? " has" : "s have"} no payment received from the sales rep.</strong> The rep should hand in the money before commission is paid:</div>}
      <ul style={{ margin: "6px 0 8px", paddingLeft: 18, maxHeight: 130, overflowY: "auto" }}>
        {gate.lines.map((l) => <li key={l.invoiceId}>{l.invoiceId}{l.customer ? " - " + l.customer : ""}{l.total ? " (" + fmtRemitMoney(l.total) + ")" : ""}</li>)}
      </ul>
      {gate.truncated && <div style={{ marginBottom: 6 }}>...and {gate.count - gate.lines.length} more.</div>}
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", fontWeight: 600 }}>
        <input type="checkbox" checked={!!ack} onChange={(e) => setAck(e.target.checked)} style={{ marginTop: 2 }} />
        <span>I understand these invoices have no payment received from the rep</span>
      </label>
    </div>
  );
}

// ── Admin invoice modal: the section ─────────────────────────────────────────
export function RepRemittanceSection({ inv }) {
  const invId = inv && inv.id ? String(inv.id) : "";
  const applies = isRepInvoice(inv);
  const invRef = useRef(inv);
  invRef.current = inv;
  const alive = useRef(true);
  const busyRef = useRef(false);
  const [ctx, setCtx] = useState(null);           // null = loading
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ method: "", reference: "", receivedOn: todayIso(), note: "" });
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [banner, setBanner] = useState("");
  const [confirmShop, setConfirmShop] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [revAck, setRevAck] = useState(false);
  const [commState, setCommState] = useState(null);   // null = reading; else { ok, paid, ... } | { ok:false, error }
  const gateRef = useRef(null);
  if (!gateRef.current) gateRef.current = createFetchGate();

  // A response is dropped when a newer load started or a local mark / reverse happened after it began.
  const load = useCallback(async () => {
    if (!invId) return;
    const gate = gateRef.current;
    const ticket = gate.begin();
    try {
      const r = await loadRemittanceContext(invId, invRef.current);
      if (!alive.current || !gate.isCurrent(ticket)) return;
      setCtx(r);
    } catch (e) {
      console.warn("[RepRemittanceSection] load threw for", invId, ":", e?.message || e);
      if (alive.current && gate.isCurrent(ticket)) setCtx({ ok: false, error: e?.message || String(e) });
    }
  }, [invId]);

  // Fresh read: was commission for this invoice already marked Paid? (shown before a reversal)
  const checkCommission = useCallback(async () => {
    setCommState(null);
    try {
      const r = await getCommissionPaidStateForInvoice(invId);
      if (alive.current) setCommState(r);
    } catch (e) {
      console.warn("[RepRemittanceSection] commission check threw for", invId, ":", e?.message || e);
      if (alive.current) setCommState({ ok: false, error: e?.message || String(e) });
    }
  }, [invId]);

  useEffect(() => {
    alive.current = true;
    if (!applies) return undefined;
    load();
    const deb = createDebounced(() => { load(); });
    const unsub = subscribeRemittances(() => { deb.trigger(); });
    const unsubLocal = subscribeLocalRemittanceChange(() => { deb.cancel(); load(); });
    return () => { alive.current = false; deb.cancel(); if (unsub) unsub(); unsubLocal(); };
  }, [applies, load]);

  if (!applies) return null;
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setErrs((e) => (e[k] ? { ...e, [k]: undefined } : e)); setConfirmShop(false); };
  const wrap = (children) => <div className="no-print" style={{ background: K.surface, borderTop: "1px solid " + K.border, padding: "16px clamp(12px,4vw,48px)" }}>{children}</div>;
  const title = <div style={{ fontSize: 13, fontWeight: 700, color: K.ink, marginBottom: 8 }}>Paid to us by the sales rep</div>;

  if (ctx === null) return wrap(<>{title}<div style={{ fontSize: 12, color: K.inkLight }}>Checking...</div></>);
  if (ctx.ok === false) {
    return wrap(<>{title}<div style={box(K.redLight, "#FCA5A5", K.red)}>Could not check the rep payment for this invoice ({ctx.error}). <button style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11, marginLeft: 6 }} onClick={() => { setCtx(null); load(); }}>Retry</button></div></>);
  }
  if (ctx.missingTable) {
    return wrap(<>{title}<div style={box(K.amberBg, K.amberBorder, K.amber)}>Rep payment tracking is not set up yet. Run migration 0033_invoice_rep_remittances.sql in the Supabase SQL editor, then reopen this invoice.</div></>);
  }

  const rem = ctx.remittance;
  const doReverse = async () => {
    if (busyRef.current) return;
    if (!String(reason).trim()) { setErr("A reason is required to reverse this payment."); return; }
    busyRef.current = true; setBusy(true); setErr("");
    try {
      const admin = await currentAdminIdentity();
      if (!admin.email && !admin.id) { setErr("Could not identify the signed-in admin. Reload the page and sign in again. Nothing was changed."); return; }
      const res = await performReverseRemittance({ remittance: rem, reason, admin, commissionAck: revAck });
      if (!res.ok) {
        setErr(res.error || "Reversing failed.");
        if (res.alreadyReversed) await load();
        if (res.needsCommissionAck || res.commissionCheckFailed) { setRevAck(false); await checkCommission(); }
        return;
      }
      setBanner("Reversed: the payment from the sales rep is no longer recorded for " + invId + "." + (res.mirrored ? "" : " (This device's copy will update on the next sync.)"));
      setRevOpen(false); setReason(""); setRevAck(false);
      await load();
    } catch (e) {
      console.warn("[RepRemittanceSection] reverse failed for", invId, ":", e?.message || e);
      setErr(e?.message || "Reversing failed.");
    } finally { busyRef.current = false; setBusy(false); }
  };

  if (rem) {
    return wrap(<>
      {title}
      {banner && <div style={box(K.greenBg, K.greenBorder, K.green)}>{banner}</div>}
      <div style={box(K.violetBg, K.violetBorder, K.violet)}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Received from the sales rep</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: "4px 14px" }}>
          <div><span style={{ opacity: 0.75 }}>Method: </span><strong>{methodLabel(rem.method)}</strong></div>
          <div><span style={{ opacity: 0.75 }}>Reference: </span><strong>{rem.reference || "-"}</strong></div>
          <div><span style={{ opacity: 0.75 }}>Date received: </span><strong>{rem.received_on || "-"}</strong></div>
          <div><span style={{ opacity: 0.75 }}>Amount: </span><strong>{fmtRemitMoney(rem.amount)}</strong></div>
          <div><span style={{ opacity: 0.75 }}>Marked by: </span><strong>{rem.marked_by || "-"}</strong></div>
        </div>
        {rem.note && <div style={{ marginTop: 4 }}><span style={{ opacity: 0.75 }}>Note: </span>{rem.note}</div>}
        <div style={{ marginTop: 4, opacity: 0.8 }}>{remittancePaidLabel(rem)}. This invoice shows as Paid because the sales rep handed the payment in, not because the shop paid us directly.</div>
      </div>
      {!revOpen && <button style={btnSecondary} onClick={() => { setRevOpen(true); setErr(""); setRevAck(false); checkCommission(); }}>Reverse (entered by mistake)</button>}
      {revOpen && (() => {
        const rg = reverseCommissionGate(commState);
        const revBlocked = rg.blocked || (rg.needsAck && !revAck);
        return (
        <div style={{ border: "1px solid " + K.border, borderRadius: 8, padding: 12, background: K.white }}>
          {commState === null && <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 8 }}>Checking whether commission for this invoice was already paid...</div>}
          {rg.tone === "error" && (
            <div style={box(K.redLight, "#FCA5A5", K.red)}>{rg.text} <button style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11, marginLeft: 6 }} onClick={checkCommission} disabled={busy}>Retry</button></div>
          )}
          {rg.tone === "warn" && (
            <div style={box(K.amberBg, K.amberBorder, K.amber)}>
              <strong>Commission already paid.</strong> {rg.text}
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", fontWeight: 600, marginTop: 6 }}>
                <input type="checkbox" checked={revAck} onChange={(e) => setRevAck(e.target.checked)} disabled={busy} style={{ marginTop: 2 }} />
                <span>I understand commission already paid is not changed; reverse anyway</span>
              </label>
            </div>
          )}
          <label style={label}>Reason for reversing (required; visible to the rep)</label>
          <textarea style={{ ...input, minHeight: 60, resize: "vertical" }} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} placeholder="e.g. Marked the wrong invoice" />
          <div style={{ fontSize: 11, color: K.inkLight, margin: "6px 0 8px" }}>The record is kept (status becomes reversed, nothing is deleted). The invoice goes back to unpaid on devices that showed it as paid because of this entry.</div>
          {err && <div style={box(K.redLight, "#FCA5A5", K.red)}>{err}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={btnSecondary} onClick={() => { setRevOpen(false); setErr(""); }} disabled={busy}>Cancel</button>
            <button style={{ ...btnDanger, opacity: (busy || revBlocked) ? 0.7 : 1 }} onClick={doReverse} disabled={busy || revBlocked}>{busy ? "Reversing..." : "Confirm reverse"}</button>
          </div>
        </div>
        );
      })()}
    </>);
  }

  // State A: nothing recorded yet.
  const elig = ctx.eligibility || { ok: false, reason: "" };
  if (inv.status === "cancelled") return banner ? wrap(<>{title}<div style={box(K.greenBg, K.greenBorder, K.green)}>{banner}</div></>) : null;
  if (!elig.ok) {
    return wrap(<>{title}{banner && <div style={box(K.greenBg, K.greenBorder, K.green)}>{banner}</div>}<div style={{ fontSize: 12, color: K.inkLight }}>{elig.reason}</div></>);
  }

  // "shop" = paid through the shop / Square (confirm first); "other" = marked paid by the rep's toggle
  // or an admin offline record (neutral note, no confirm); "" = not paid.
  const paySrc = paymentSourceOf(inv);
  const submit = async () => {
    if (busyRef.current) return;
    const v = validateRemittanceInput(f);
    if (!v.ok) { setErrs(v.errors); return; }
    if (paySrc === "shop" && !confirmShop) { setConfirmShop(true); return; }
    busyRef.current = true; setBusy(true); setErr(""); setErrs({});
    try {
      const admin = await currentAdminIdentity();
      if (!admin.email && !admin.id) { setErr("Could not identify the signed-in admin. Reload the page and sign in again. Nothing was recorded."); return; }
      // The amount on screen may be stale (an approved rep adjustment): re-read at Confirm time and stop if it moved.
      const fresh = await loadRemittanceContext(invId, invRef.current);
      if (!fresh.ok) { setErr("The invoice could not be re-read before saving (" + fresh.error + "). Nothing was recorded. Try again."); return; }
      if (fresh.missingTable || fresh.remittance || !(fresh.eligibility && fresh.eligibility.ok)) { setCtx(fresh); return; }
      const changed = amountChangedMessage(ctx.amount, fresh.amount);
      if (changed) { setCtx(fresh); setErr(changed); return; }
      const res = await performMarkRemitted({ inv: { id: inv.id, repId: inv.repId }, form: v.value, admin });
      if (!res.ok) { setErr(res.error || "Recording failed."); if (res.alreadyReceived) await load(); return; }
      const extra = [].concat(res.warnings || []);
      if (!res.mirrored) extra.push("This device's copy will update on the next sync.");
      setBanner("Recorded: received from the sales rep, " + remittancePaidLabel(res.row) + ", " + fmtRemitMoney(res.row.amount) + "." + (extra.length ? " " + extra.join(" ") : ""));
      setOpen(false); setConfirmShop(false);
      setF({ method: "", reference: "", receivedOn: todayIso(), note: "" });
      await load();
    } catch (e) {
      console.warn("[RepRemittanceSection] mark failed for", invId, ":", e?.message || e);
      setErr(e?.message || "Recording failed.");
    } finally { busyRef.current = false; setBusy(false); }
  };

  return wrap(<>
    {title}
    {banner && <div style={box(K.greenBg, K.greenBorder, K.green)}>{banner}</div>}
    {!open && (
      <>
        <div style={{ fontSize: 12, color: K.inkLight, marginBottom: 8 }}>Not received from the sales rep yet. Use this when the rep handed in the payment they collected from the shop.</div>
        <button style={btnPrimary} onClick={() => { setOpen(true); setErr(""); setConfirmShop(false); setF((p) => ({ ...p, receivedOn: todayIso() })); }}>Mark paid to us by sales rep</button>
      </>
    )}
    {open && (
      <div style={{ border: "1px solid " + K.border, borderRadius: 8, padding: 12, background: K.white }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={label}>How did the rep pay us? *</label>
            <select style={{ ...input, borderColor: errs.method ? K.red : K.border }} value={f.method} onChange={(e) => set("method", e.target.value)} disabled={busy}>
              <option value="">Choose...</option>
              {REMIT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {errs.method && <div style={{ fontSize: 11, color: K.red, marginTop: 2 }}>{errs.method}</div>}
          </div>
          <div>
            <label style={label}>Reference (check no. / transfer id)</label>
            <input style={{ ...input, borderColor: errs.reference ? K.red : K.border }} value={f.reference} onChange={(e) => set("reference", e.target.value)} disabled={busy} placeholder="optional" />
            {errs.reference && <div style={{ fontSize: 11, color: K.red, marginTop: 2 }}>{errs.reference}</div>}
          </div>
          <div>
            <label style={label}>Date received *</label>
            <input type="date" max={todayIso()} style={{ ...input, borderColor: errs.receivedOn ? K.red : K.border }} value={f.receivedOn} onChange={(e) => set("receivedOn", e.target.value)} disabled={busy} />
            {errs.receivedOn && <div style={{ fontSize: 11, color: K.red, marginTop: 2 }}>{errs.receivedOn}</div>}
          </div>
          <div>
            <label style={label}>Amount (invoice total)</label>
            <input style={{ ...input, background: K.surface }} value={ctx.amount === null || ctx.amount === undefined ? "unavailable" : fmtRemitMoney(ctx.amount)} readOnly disabled />
          </div>
        </div>
        <label style={label}>Note (optional; visible to the rep)</label>
        <textarea style={{ ...input, minHeight: 50, resize: "vertical", borderColor: errs.note ? K.red : K.border }} value={f.note} onChange={(e) => set("note", e.target.value)} disabled={busy} />
        {errs.note && <div style={{ fontSize: 11, color: K.red, marginTop: 2 }}>{errs.note}</div>}
        {paySrc === "other" && (
          <div style={{ ...box(K.surface, K.border, K.inkMid), marginTop: 10 }}>
            This invoice is already marked paid (by the rep or an offline record). Recording the remittance keeps the payment method and reference on file; it only adds the rep payment.
          </div>
        )}
        {paySrc === "shop" && confirmShop && (
          <div style={{ ...box(K.amberBg, K.amberBorder, K.amber), marginTop: 10 }}>
            <strong>This invoice already shows as paid by the shop; record the rep remittance anyway?</strong>{inv.paidMethod ? " (" + inv.paidMethod + ")" : ""} The invoice keeps its existing payment details; the rep payment is recorded separately.
          </div>
        )}
        {err && <div style={{ ...box(K.redLight, "#FCA5A5", K.red), marginTop: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <button style={btnSecondary} onClick={() => { setOpen(false); setErr(""); setConfirmShop(false); }} disabled={busy}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }} onClick={submit} disabled={busy}>{busy ? "Saving..." : (confirmShop ? "Yes, record it" : "Confirm")}</button>
        </div>
      </div>
    )}
  </>);
}

